import { CheckCircle2, AlertTriangle, Bot } from "lucide-react";
import { CoverImage } from "@/components/CoverImage";
import { CodeBadge } from "@/components/StatusBadge";
import { artistDisplay } from "./ArtistList";
import { t } from "@/i18n";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0 text-sm">
    <span className="text-muted-foreground">{t(label)}</span><span className="text-right font-medium truncate">{value || "—"}</span>
  </div>
);

export const StepDispatch = ({ rel, settings, issues }) => {
  const checks = [
    [!!rel.title && (rel.primary_artists || []).some((n) => n.trim()), "Título y artista"],
    [!!rel.cover_file_id, "Portada cargada"],
    [rel.tracks.length > 0 && rel.tracks.every((t) => t.title), "Tracks con título"],
    [rel.tracks.every((t) => t.audio_file_id && /\.(wav|flac)$/i.test(t.audio_filename || "")), "Audio WAV/FLAC en todos los tracks (no se acepta MP3)"],
    [rel.tracks.every((t) => t.contributors.some((c) => ["Composer", "Lyricist", "Songwriter", "Arranger"].includes(c.role)) && t.contributors.some((c) => ["Producer", "Co-Producer", "Mixing Engineer", "Mastering Engineer", "Recording Engineer"].includes(c.role))), "Compositor y productor en cada track (obligatorio para distribuir)"],
    [rel.tracks.every((t) => t.contributors.filter((c) => ["Composer", "Lyricist", "Songwriter"].includes(c.role)).every((c) => c.name.trim().split(/\s+/).length >= 2)), "Compositores/letristas con nombre y apellido (requisito del distribuidor)"],
    [rel.stores.length > 0, "Al menos una tienda"],
    [!!rel.live_date, "Live date definida"],
  ];
  const mode = settings ? (settings.has_saved_session || settings.has_session_cookie || settings.has_password ? "REAL" : "SIMULACIÓN") : "—";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 panel p-6 flex gap-5">
        <CoverImage fileId={rel.cover_file_id} className="h-32 w-32 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="eyebrow">{rel.release_type}</p>
          <h3 className="font-display text-xl font-bold truncate">{rel.title || t("Sin título")}</h3>
          <p className="text-muted-foreground truncate" data-testid="dispatch-artist-display">{artistDisplay(rel.primary_artists, rel.featured_artists) || rel.artist_name}</p>
          <div className="mt-3"><CodeBadge code={rel.upc} kind="upc" placeholder={settings ? "UPC lo asignará Downtown" : "El UPC se asignará al distribuir"} /></div>
          <div className="mt-3">
            <Row label="Live date" value={rel.live_date} />
            <Row label="Género" value={rel.primary_genre} />
            <Row label="Territorios" value={rel.territories.mode === "worldwide" ? t("Mundial") : t("{n} países", { n: rel.territories.countries.length })} />
            <Row label="Tiendas" value={`${rel.stores.length}`} />
            <Row label="Tracks" value={`${rel.tracks.length}`} />
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 space-y-4">
        {issues?.length > 0 && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 space-y-1.5" data-testid="precheck-issues">
            <p className="font-medium text-sm text-destructive">{t("Revisa estos puntos antes de enviar")}</p>
            <ul className="text-sm space-y-1 list-disc pl-5">{issues.map((i, k) => <li key={k} data-testid={`precheck-issue-${i.code}`}>{t(i.template || i.message, i.params)}</li>)}</ul>
          </div>
        )}
        <div className="panel p-6">
          <h3 className="font-display font-semibold mb-3">{t("Checklist antes de enviar")}</h3>
          <ul className="space-y-2" data-testid="dispatch-checklist">
            {checks.map(([ok, label]) => (
              <li key={label} className="flex items-center gap-2 text-sm" data-ok={ok}>
                {ok ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                <span className={ok ? "" : "text-muted-foreground"}>{t(label)}</span>
              </li>
            ))}
          </ul>
        </div>
        {settings && <div className="panel p-6 flex gap-4">
          <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0"><Bot className="h-5 w-5" /></div>
          <div className="text-sm space-y-1">
            <p className="font-medium">{t("Bot Downtown · modo")} <span className="font-mono text-primary" data-testid="dispatch-bot-mode">{mode}</span></p>
            <p className="text-muted-foreground">{t("Al enviar, el bot abrirá Downtown, iniciará sesión y te pedirá el código de verificación en la consola del bot. Los UPC / ISRC devueltos se asignan automáticamente a este lanzamiento.")}</p>
            {mode === "SIMULACIÓN" && <p className="text-amber-500">{t("Sin credenciales de Downtown en Ajustes: el bot correrá en simulación y generará códigos de prueba.")}</p>}
          </div>
        </div>}
        {!settings && <div className="panel p-6 text-sm text-muted-foreground" data-testid="dispatch-artist-note">{t("Al pulsar «Guardar y enviar a distribución» validamos tu lanzamiento, damos de alta a los artistas y colaboradores y lo enviamos a las tiendas. Verás el estado por plataforma cuando sea aprobado.")}</div>}
      </div>
    </div>
  );
};
